import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Phone as PhoneIcon, Mail as MailIcon, MessageCircle, Phone } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CarBand from "@/components/CarBand";
import TrustBar from "@/components/TrustBar";
import Testimonials from "@/components/Testimonials";
import HeroSlideshow from "@/components/HeroSlideshow";
import usePageMeta from "@/hooks/usePageMeta";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const HERO_IMG = null; // moved into HeroSlideshow component

const PRODUCTS = [
  {
    key: "auto",
    label: "Auto",
    number: "01",
    title: "Auto Insurance",
    desc: "Comprehensive, third-party, and specialist coverage for everyday drivers, classic collectors, and multi-vehicle households.",
    img: "https://images.unsplash.com/photo-1583002414111-fce7b1102e13",
  },
  {
    key: "home",
    label: "Property",
    number: "02",
    title: "Property Insurance",
    desc: "Building, contents, liability, and specialty items — coverage written to protect what matters most.",
    img: "https://images.pexels.com/photos/7031408/pexels-photo-7031408.jpeg",
  },
  {
    key: "commercial",
    label: "Commercial",
    number: "03",
    title: "Commercial Insurance",
    desc: "General liability, property, professional indemnity, and workers coverage — tailored to trades, retail, offices, and beyond.",
    img: "https://images.pexels.com/photos/1313534/pexels-photo-1313534.jpeg",
  },
];

const STEPS = [
  { n: "01", t: "Tell us the essentials", d: "Under a minute. What you own, what you drive, where you live." },
  { n: "02", t: "We shop the market", d: "We pull competing quotes from the carriers most likely to underwrite you well." },
  { n: "03", t: "You choose. We handle it.", d: "A single tailored recommendation, in plain language. Paperwork, changes, and questions stay with us." },
  { n: "04", t: "Every renewal, we take a fresh look", d: "Ahead of each renewal we apply loyalty discounts, claims-free credits, and re-check the market — so your policy keeps working for you year after year." },
];

const FAQS = [
  { q: "Can you actually beat my current premium?", a: "That's the entire reason to use a broker. We have access to every major carrier in Ontario — 40+ market partners — and shop your risk across all of them. If a better price exists, we'll find it. If you already have the best deal, we'll tell you honestly." },
  { q: "Do you review my policy every year at renewal?", a: "Yes — every single year. Ahead of your renewal, we take a fresh look at your policy and apply everything available: loyalty discounts, claims-free credits, safe-driver bonuses, multi-policy savings, and a fresh market comparison. If we can rewrite it for a better rate, we bring the option to you. If your current policy is already the best fit, we'll tell you honestly." },
  { q: "Are you tied to any single insurance company?", a: "No — and that's the difference. We're fully independent. We shop your risk across 40+ carriers with zero pressure to steer you toward a particular company. Our loyalty is to you, not to any insurer." },
  { q: "How long does a quote take?", a: "Under a minute on your side. One business day on ours to come back with real numbers. Complex commercial risks may take 2–3 days as we consult multiple markets — but nothing is dragged out." },
  { q: "Can I bundle auto and home?", a: "Yes — and if you don't, you're leaving money on the table. Multi-policy discounts typically save 15–25% and lower your premium without touching your coverage. It's the fastest win in insurance." },
  { q: "How do I switch from my current broker or insurer?", a: "We handle the switch for you. Send us your current policy and we'll do the market comparison, prepare fresh quotes, and coordinate the cancellation timing so you're never left uncovered — and never left doing paperwork." },
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.19, 1, 0.22, 1] },
};

export default function Landing() {
  const nav = useNavigate();

  usePageMeta({
    title: "AlwaysInsured — Auto, Home & Commercial Insurance Broker Serving All of Ontario",
    description: "Licensed insurance brokerage serving all of Ontario. Personal service on auto, home, and commercial insurance across 40+ carriers. Free quote in under a minute.",
  });

  return (
    <div data-testid="landing-page" className="bg-forest-900 text-bone-100 min-h-screen">
      <Header />

      {/* HERO */}
      <section className="pt-36 md:pt-44 pb-20 md:pb-28 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <motion.div className="lg:col-span-7" {...fadeUp}>
            <h1 className="font-serif text-bone-100 text-[3.2rem] sm:text-6xl md:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tighter">
              Insurance that <br />
              <span className="italic">stays</span> insured.
            </h1>
            <div className="overline text-terracotta-500 mt-6">
              Licensed brokerage · Serving all of Ontario
            </div>
            <div
              data-testid="hero-motto"
              className="mt-6 font-serif italic text-2xl md:text-3xl text-terracotta-500 tracking-tight"
            >
              Always there, in all ways possible.
            </div>
            <p className="mt-6 max-w-xl text-lg md:text-xl text-bone-100/60 leading-relaxed font-light">
              We broker <strong className="font-medium">auto, home, and commercial</strong> policies — then <strong className="font-medium text-bone-100/80">review your renewal every year</strong> to unlock loyalty discounts and rewrite for savings whenever we can.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-wrap">
              <button
                data-testid="hero-get-quote-btn"
                onClick={() => nav("/quote")}
                className="group inline-flex items-center gap-3 bg-terracotta-500 text-forest-900 hover:bg-terracotta-400 transition-all px-8 py-4 text-base tracking-wide font-medium"
              >
                Start a quote
                <ArrowUpRight
                  size={18}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </button>
              <a
                data-testid="hero-whatsapp-btn"
                href="https://wa.me/14163153679?text=Hi%2C%20I%20want%20a%20free%20insurance%20quote."
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 border border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white transition-all px-6 py-4 text-base font-medium"
              >
                <MessageCircle size={18} fill="currentColor" strokeWidth={0} />
                WhatsApp
              </a>
              <a
                data-testid="hero-call-btn"
                href="tel:+14163153679"
                className="group inline-flex items-center gap-2 text-bone-100 hover:text-terracotta-400 transition-colors"
              >
                <Phone size={16} />
                <span className="text-base tracking-wide font-medium">(416) 315-3679</span>
              </a>
            </div>
            <div className="mt-4 inline-flex items-center gap-2.5 text-sm text-terracotta-500">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-terracotta-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-terracotta-500"></span>
              </span>
              <span className="tracking-wide">Quote in under a minute · Call or WhatsApp anytime</span>
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-5 relative"
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
          >
            <HeroSlideshow />
            <div className="absolute -top-6 -left-6 bg-forest-800 border border-bone-100/20 px-5 py-4 flex items-center gap-3 z-10">
              <ShieldCheck size={20} className="text-terracotta-500" />
              <div>
                <div className="font-serif text-lg text-bone-100 leading-none">Broker of record</div>
                <div className="overline text-bone-100/60 mt-1">Licensed</div>
              </div>
            </div>
            <div className="absolute -bottom-5 -right-5 bg-terracotta-500 text-forest-900 px-5 py-4 max-w-[190px] z-10">
              <div className="font-serif text-xl leading-tight">Same day, or next.</div>
              <div className="overline opacity-80 mt-1">Human answer</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* METRICS / TRUST */}
      <section className="px-6 md:px-12 lg:px-16 pb-24">
        <div className="max-w-[1400px] mx-auto border-t border-bone-100/15 pt-16 grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-6">
          {[
            { n: "Auto", l: "Home · Commercial" },
            { n: "40+", l: "Carrier partners" },
            { n: "1 day", l: "Typical quote turnaround" },
            { n: "100%", l: "Independent advice" },
          ].map((m, i) => (
            <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}>
              <div className="font-sans font-light text-5xl md:text-6xl text-bone-100 tracking-tight leading-none">
                {m.n}
              </div>
              <div className="overline text-bone-100/50 mt-4">{m.l}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CAR BAND — cinematic accent */}
      <CarBand variant="dark" />

      {/* TRUST BAR — real numbers */}
      <TrustBar />

      {/* PRODUCTS */}
      <section id="products" className="px-6 md:px-12 lg:px-16 py-24 bg-forest-800/40">
        <div className="max-w-[1400px] mx-auto">
          <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-bone-100 tracking-tight max-w-2xl leading-tight">
                Which one is <span className="italic text-terracotta-400">yours?</span>
              </h2>
              <div className="overline text-terracotta-500 mt-4">Choose your coverage · Submit here</div>
            </div>
            <p className="max-w-md text-bone-100/60 leading-relaxed">
              Tap a category below to start your quote — <strong className="text-terracotta-400">the form takes under a minute</strong>. Not sure which fits? Call us at <a href="tel:+14163153679" className="text-bone-100 underline underline-offset-4 hover:text-terracotta-400">(416) 315‑3679</a>.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
            {PRODUCTS.map((p, i) => (
              <motion.div
                key={p.key}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                className="group bg-forest-800 border border-bone-100/15 hover:border-terracotta-500 hover:-translate-y-1 transition-all duration-500 flex flex-col"
                data-testid={`product-card-${p.key}`}
              >
                <div className="aspect-[4/5] overflow-hidden relative">
                  <img
                    src={p.img}
                    alt={`${p.title} — AlwaysInsured coverage`}
                    className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]"
                  />
                  <div className="absolute top-4 left-4 bg-forest-900/90 backdrop-blur border border-bone-100/15 px-3 py-1.5 overline text-terracotta-400">
                    {p.number} · Submit here
                  </div>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <h3 className="font-serif text-3xl md:text-[2rem] text-bone-100 leading-tight mb-3">
                    {p.title}
                  </h3>
                  <p className="text-bone-100/60 text-sm leading-relaxed mb-6 flex-1">{p.desc}</p>
                  <button
                    data-testid={`product-quote-${p.key}`}
                    onClick={() => nav(`/quote?type=${p.key}`)}
                    className="w-full mt-auto inline-flex items-center justify-between bg-terracotta-500 text-forest-900 hover:bg-terracotta-400 transition-colors px-5 py-3.5 font-medium text-sm tracking-wide"
                  >
                    Get a {p.label.toLowerCase()} quote
                    <ArrowUpRight size={18} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div {...fadeUp} className="lg:col-span-4">
            <h2 className="font-serif text-4xl md:text-5xl text-bone-100 leading-tight tracking-tight">
              Four steps. <br /><span className="italic">Then we keep working.</span>
            </h2>
            <div className="overline text-terracotta-500 mt-4">How it works</div>
          </motion.div>
          <div className="lg:col-span-8 space-y-0">
            {STEPS.map((s, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                className="grid grid-cols-12 gap-6 border-t border-bone-100/15 py-8 first:border-t-0"
              >
                <div className="col-span-2 font-sans font-light text-4xl md:text-5xl text-terracotta-500 leading-none">{s.n}</div>
                <div className="col-span-10">
                  <h3 className="font-serif text-2xl md:text-3xl text-bone-100 mb-2 leading-tight">{s.t}</h3>
                  <p className="text-bone-100/60 leading-relaxed max-w-xl">{s.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* FAQ */}
      <section id="faq" className="px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div {...fadeUp} className="lg:col-span-4">
            <h2 className="font-serif text-4xl md:text-5xl text-bone-100 tracking-tight leading-tight">
              Straight <span className="italic">answers.</span>
            </h2>
            <div className="overline text-terracotta-500 mt-4">FAQ</div>
          </motion.div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-bone-100/15 border-t-0"
                >
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="text-left font-serif text-xl md:text-2xl text-bone-100 hover:no-underline py-6"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-bone-100/60 leading-relaxed text-base pb-6 max-w-2xl">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CONTACT US */}
      <section id="contact-us" className="px-6 md:px-12 lg:px-16 py-24 md:py-28 border-t border-bone-100/10">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-bone-100 tracking-tight leading-[1.05]">
              Reach us. <br /><span className="italic text-terracotta-400">Any way you like.</span>
            </h2>
            <div className="overline text-terracotta-500 mt-4">Contact us</div>
            <p className="mt-6 text-bone-100/60 max-w-md leading-relaxed">
              Reach us any day of the week. This number is also on <strong className="text-bone-100">WhatsApp</strong> — send a message for assistance and we'll get right back.
            </p>
          </motion.div>

          <div className="lg:col-span-7 space-y-4">
            <motion.a
              {...fadeUp}
              href="tel:+14163153679"
              data-testid="contact-phone"
              className="flex items-center justify-between border border-bone-100/15 hover:border-terracotta-500 hover:bg-bone-100/5 transition-all px-6 md:px-8 py-6 group"
            >
              <div className="flex items-center gap-4">
                <span className="shrink-0 text-terracotta-500">
                  <PhoneIcon size={30} strokeWidth={1.6} />
                </span>
                <div>
                  <div className="font-serif text-3xl md:text-4xl text-bone-100 tracking-tight">(416) 315&#8209;3679</div>
                  <div className="overline text-bone-100/50 mt-2">Call</div>
                </div>
              </div>
              <ArrowUpRight size={24} className="text-terracotta-500 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" />
            </motion.a>

            <motion.a
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.08 }}
              href="https://wa.me/14163153679"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="flex items-center justify-between border border-bone-100/15 hover:border-[#25D366] hover:bg-bone-100/5 transition-all px-6 md:px-8 py-6 group"
            >
              <div className="flex items-center gap-4">
                <span className="text-[#25D366] shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="34" height="34" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zm-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </span>
                <div>
                  <div className="font-serif text-3xl md:text-4xl text-bone-100 tracking-tight">Message us anytime</div>
                  <div className="text-sm text-bone-100/60 mt-1.5">Same number · available on WhatsApp for assistance</div>
                  <div className="overline text-bone-100/50 mt-2">WhatsApp</div>
                </div>
              </div>
              <ArrowUpRight size={24} className="text-terracotta-500 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" />
            </motion.a>

            <motion.a
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.12 }}
              href="https://instagram.com/alwaysinsured"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram"
              className="flex items-center justify-between border border-bone-100/15 hover:border-[#DD2A7B] hover:bg-bone-100/5 transition-all px-6 md:px-8 py-6 group"
            >
              <div className="flex items-center gap-4">
                <span className="shrink-0 text-[#DD2A7B]">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="2" y="2" width="20" height="20" rx="5"/>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37Z"/>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                  </svg>
                </span>
                <div>
                  <div className="font-serif text-3xl md:text-4xl text-bone-100 tracking-tight">@alwaysinsured</div>
                  <div className="text-sm text-bone-100/60 mt-1.5">Follow us for tips & updates</div>
                  <div className="overline text-bone-100/50 mt-2">Instagram</div>
                </div>
              </div>
              <ArrowUpRight size={24} className="text-terracotta-500 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" />
            </motion.a>

            <motion.a
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.16 }}
              href="mailto:Alwaysinsured@hotmail.com"
              data-testid="contact-email"
              className="flex items-center justify-between border border-bone-100/15 hover:border-terracotta-500 hover:bg-bone-100/5 transition-all px-6 md:px-8 py-6 group"
            >
              <div className="flex items-center gap-4 min-w-0">
                <span className="shrink-0 text-terracotta-500">
                  <MailIcon size={30} strokeWidth={1.6} />
                </span>
                <div className="min-w-0">
                  <div className="font-serif text-2xl md:text-3xl text-bone-100 tracking-tight break-all">Alwaysinsured@hotmail.com</div>
                  <div className="overline text-bone-100/50 mt-2">Email</div>
                </div>
              </div>
              <ArrowUpRight size={24} className="text-terracotta-500 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1 shrink-0" />
            </motion.a>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="px-6 md:px-12 lg:px-16 pb-24">
        <div className="max-w-[1400px] mx-auto bg-forest-800 border border-bone-100/15 p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl text-bone-100 tracking-tight max-w-xl leading-tight">
              Under a minute. <span className="italic">A better policy by tomorrow.</span>
            </h3>
            <div className="overline text-terracotta-500 mt-3">Ready when you are</div>
          </div>
          <button
            data-testid="cta-strip-quote-btn"
            onClick={() => nav("/quote")}
            className="bg-terracotta-500 text-forest-900 hover:bg-terracotta-400 transition-colors px-8 py-4 inline-flex items-center gap-3 whitespace-nowrap font-medium"
          >
            Start a quote <ArrowUpRight size={18} />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
